<template>
  <div class="motives-grid container mt40">
    <h2 class="motives-grid__title">{{ title }}</h2>
    <ul class="motives-grid__grid">
      <li v-for="item in motives" :key="item.name">
        <NuxtLink v-if="item.isAsyncLink" @click="getAsyncLink(item)">
          <NuxtTransformImage
            :src="`${srcSource}${item.name}.png`"
            :alt="item.name"
            fit="cover"
            sizes="sm:165px md:183px lg:165px"
          />
          <p class="motives-grid__text">{{ $t(`${translationPath}.${item.name}`) }}</p>
        </NuxtLink>
        <NuxtLink v-else :to="item.link">
          <NuxtTransformImage
            :src="`${srcSource}${item.name}.png`"
            :alt="item.name"
            fit="cover"
            sizes="sm:165px md:183px lg:165px"
          />
          <p class="motives-grid__text">{{ $t(`${translationPath}.${item.name}`) }}</p>
        </NuxtLink>
      </li>
    </ul>
    <SButton class="motives-grid__cta centered" :link="buttonLink"> {{ buttonText }}</SButton>
  </div>
</template>

<script setup lang="ts">
import { useProductStore } from '@/stores/product';
import { useRuntimeConfig } from '#imports';

const NuxtTransformImage = defineAsyncComponent(() => import('@/components/molecules/NuxtTransformImage.vue'));
const SButton = defineAsyncComponent(() => import('@/components/molecules/buttons/SButton.vue'));

const { $router } = useNuxtApp();

const productStore = useProductStore();
const runtimeConfig = useRuntimeConfig();

interface Motive {
  name?: String;
  link?: String;
  isAsyncLink?: Boolean;
  themeUuid?: string;
}

interface Link {
  path: string;
  query?: Record<string, string>;
}

interface Props {
  title: string;
  motives: Motive[];
  showButton?: Boolean;
  buttonLink: string | Link;
  srcSource: string;
  translationPath: string;
  buttonText: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: null,
  motives: [],
  showButton: true,
  buttonLink: null,
  srcSource: null,
  translationPath: null,
  buttonText: null
});

const linkRef = ref(null);

async function getAsyncLink(item) {
  const result = await productStore.fetchProducts(
    {
      page: 1,
      perPage: 1,
      themeUuid: item.themeUuid,
      taxonId: 23,
      mainTaxonCodes: 'online_events'
    },
    true
  );
  return `/events/${result?.[0]?.slug}`;
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/font-size.scss' as *;
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;

.motives-grid {
  text-align: center;

  ul {
    list-style-type: none;
    padding-left: 0;
    li {
      width: 100%;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(10px, 1fr));
    column-gap: 20px;
    margin: 20px;
    @include bp(sm) {
      grid-template-columns: repeat(3, 1fr);
    }
    @include bp(md) {
      grid-template-columns: repeat(4, 1fr);
      margin: 40px 20px;
    }
    @include bp(lg) {
      grid-template-columns: repeat(4, 1fr);
      margin: 40px 0px 10px 0px;
    }
  }
  &__title {
    font-weight: 500;
    @include font-size(30px);
    line-height: 130%;
    margin-bottom: 20px;
    text-align: left;
    margin: 0px 20px;
    @include bp(sm-up) {
      margin-left: 20px;
    }
    @include bp(md) {
      margin: 40px 20px;
    }
    @include bp(lg) {
      margin: 40px 0px;
    }
  }

  &__text {
    font-weight: 500;
    @include font-size(18px);
    line-height: 130%;
    margin-bottom: 30px;
    text-align: left;
  }

  &__cta {
    margin: auto;
    position: relative;
    text-align: center;
    margin-bottom: 20px;
  }

  img {
    width: 100%;
    height: 248px;
    object-fit: cover;
    cursor: pointer;
    @include bp(sm-up) {
      width: 100%;
    }

    @include bp(lg) {
      height: 287px;
    }
  }
}
</style>
